import {defineStore} from 'pinia'
import {PermissionType} from '@toolify/server/src/models/mongoose/PermissionModel/enum/PermissionType'
import {
  IPermissionResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IPermissionResponseInstance'
import {$, $$, $computed, $ref} from '@vue-macros/reactivity-transform/macros'
import {
  IWorkspaceResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IWorkspaceResponseInstance'
import {useAuthStore} from '@toolify/client/src/stores/AuthStore/useAuthStore'
import {watch} from 'vue'
import {useMeApiStore} from '@toolify/client/src/stores/api/MeApiStore/useMeApiStore'
import {GlobalSocketEventType} from '@toolify/server/src/services/SocketService/enum/GlobalSocketEventType'
import {ResourceType} from '@toolify/server/src/models/mongoose/PermissionModel/enum/ResourceType'
import {useSocketStore} from '@toolify/client/src/stores/SocketStore/useSocketStore'
import {
  IEntityJoinedResourceEmitPayload,
} from '@toolify/server/src/services/SocketService/strategies/GlobalSocketEventTypeStrategy/IEntityJoinedResourceEmitPayload'
import {
  IEntityLeftResourceEmitPayload,
} from '@toolify/server/src/services/SocketService/strategies/GlobalSocketEventTypeStrategy/IEntityLeftResourceEmitPayload'
import {
  IEntitiesInResourceEmitPayload,
} from '@toolify/server/src/services/SocketService/strategies/GlobalSocketEventTypeStrategy/IEntitiesInResourceEmitPayload'
import {
  IWorkspaceMemberDeletedEmitPayload,
} from '@toolify/server/src/services/SocketService/strategies/GlobalSocketEventTypeStrategy/IWorkspaceMemberDeletedEmitPayload'
import {
  IResourceAccessRevokedEmitPayload,
} from '@toolify/server/src/services/SocketService/strategies/GlobalSocketEventTypeStrategy/IResourceAccessRevokedEmitPayload'
import {IResponseEntity} from '@toolify/server/src/services/EntityService/types/IResponseEntity'
import {getEntityKey} from '@toolify/client/src/modules/entities/functions/getEntityKey'
import {getEntityKeyByEntity} from '@toolify/client/src/modules/entities/functions/getEntityKeyByEntity'
import {
  IUserUpdatedEmitPayload,
} from '@toolify/server/src/services/SocketService/strategies/GlobalSocketEventTypeStrategy/IUserUpdatedEmitPayload'
import {mapUserResponseInstance} from '@toolify/client/src/modules/api/singletons/mapUserResponseInstance'
import {IToJson} from '@toolify/client/src/types/IToJson'
import {useRouter} from 'vue-router'
import {useLocalRoute} from '@toolify/client/src/modules/routes/composables/useLocalRoute'
import {useMessages} from '@toolify/client/src/modules/notifications/composables/useMessages'
import {useActivityItemSocketListener} from '@toolify/client/src/composables/useActivityItemSocketListener'
import {ActivityItemType} from '@toolify/server/src/models/mongoose/ActivityItemModel/enum/ActivityItemType'
import {RouteName} from '@toolify/client/src/setup/router/enum/RouteName'
import {IGetMyWorkspacesRequestInterfaces} from '@toolify/server/src/controllers/UserController/validators/GetMyWorkspaces/types/IGetMyWorkspacesRequestInterfaces'
import {EntityType} from '@toolify/server/src/services/EntityService/enum/EntityType'
import {isGodModeEnabled} from '@toolify/client/src/stores/AdminRuntimeStore/functions/isGodModeEnabled'
import {useCacheStore} from '@toolify/client/src/stores/CacheStore/useCacheStore'
import {useGlobalSocketResourceState} from '@toolify/client/src/composables/useGlobalSocketResourceState'
import {i18n} from '@toolify/client/src/setup/i18n'

const MY_WORKSPACES_PAGE_SIZE = 20

export const useWorkspaceStore = defineStore('workspace', () => {
  const authStore = useAuthStore()
  const meApiStore = useMeApiStore()
  const socketStore = useSocketStore()
  const cacheStore = useCacheStore()
  const router = useRouter()
  const messages = useMessages()
  const route = useLocalRoute()
  const {
    joinResource: joinResourceGlobally,
    leaveResource: leaveResourceGlobally,
  } = $(useGlobalSocketResourceState())
  const workspaceRoomHolderId = Symbol()
  const joinedWorkspaceRoomIds = new Set<string>()

  let currentWorkspace = $ref<IWorkspaceResponseInstance>(null)
  let myWorkspaces = $ref<IWorkspaceResponseInstance[]>(null)
  let myWorkspacesHasMore = $ref(false)
  let myWorkspacesSkip = $ref(0)
  let isLoadingMyWorkspaces = $ref(false)
  let workspacePermissionMap = $ref<Map<PermissionType, unknown>>(null)
  let entitiesInResourceMap = $ref<Map<string, IResponseEntity[]>>(new Map())
  let hasJoinedAllWorkspaces = $ref(false)
  let getMyWorkspacesPromise = $ref<Promise<IGetMyWorkspacesRequestInterfaces['response']>>(null)
  const revokedAtByWorkspaceId = new Map<string, number>()

  function initialize() {
    socketStore.socket.on(GlobalSocketEventType.UserJoinedResource, onUserJoinedResource)
    socketStore.socket.on(GlobalSocketEventType.UserLeftResource, onUserLeftResource)
    socketStore.socket.on(GlobalSocketEventType.UsersInResource, onUsersInResource)
    socketStore.socket.on(GlobalSocketEventType.UserUpdated, onUserUpdated)
    socketStore.socket.on(GlobalSocketEventType.WorkspaceMemberDeleted, onWorkspaceMemberDeleted)
    socketStore.socket.on(GlobalSocketEventType.ResourceAccessRevoked, onResourceAccessRevoked)
    start()
  }

  function dispose() {
    socketStore.socket.off(GlobalSocketEventType.UserJoinedResource, onUserJoinedResource)
    socketStore.socket.off(GlobalSocketEventType.UserLeftResource, onUserLeftResource)
    socketStore.socket.off(GlobalSocketEventType.UsersInResource, onUsersInResource)
    socketStore.socket.off(GlobalSocketEventType.UserUpdated, onUserUpdated)
    socketStore.socket.off(GlobalSocketEventType.WorkspaceMemberDeleted, onWorkspaceMemberDeleted)
    socketStore.socket.off(GlobalSocketEventType.ResourceAccessRevoked, onResourceAccessRevoked)
    stop()
  }

  async function fetchMyWorkspacesPage(shouldAppend = false) {
    if(isLoadingMyWorkspaces) {
      return
    }
    if(authStore.currentEntity?.entityType !== EntityType.User) {
      myWorkspaces = []
      myWorkspacesHasMore = false
      myWorkspacesSkip = 0
      return
    }
    isLoadingMyWorkspaces = true
    try {
      const skip = shouldAppend ? myWorkspacesSkip : 0
      const requestedAt = Date.now()
      const response = await meApiStore.getMyWorkspaces(skip, MY_WORKSPACES_PAGE_SIZE)
      const fetchedWorkspaces = response.workspaces.filter(workspace => {
        return !(revokedAtByWorkspaceId.get(workspace.id) > requestedAt)
      })
      if(shouldAppend && myWorkspaces) {
        myWorkspaces = myWorkspaces.concat(fetchedWorkspaces)
      } else {
        myWorkspaces = fetchedWorkspaces
      }
      myWorkspacesHasMore = response.hasMore
      // Advance by requested page size so soft-deleted holes in the id list don't stall pagination.
      myWorkspacesSkip = skip + MY_WORKSPACES_PAGE_SIZE
      return response
    } finally {
      isLoadingMyWorkspaces = false
    }
  }

  async function ensureMyWorkspaces() {
    if(myWorkspaces) {
      return 
    }
    if(authStore.currentEntity?.entityType !== EntityType.User) {
      myWorkspaces = []
      return
    }
    if(getMyWorkspacesPromise) {
      await getMyWorkspacesPromise
      return
    }
    // eslint-disable-next-line no-async-promise-executor
    getMyWorkspacesPromise = new Promise(async (resolve, reject) => {
      try {
        const response = await fetchMyWorkspacesPage(false)
        if(!isGodModeEnabled()) {
          while(myWorkspacesHasMore) {
            const previousLength = myWorkspaces?.length ?? 0
            await fetchMyWorkspacesPage(true)
            if(!myWorkspaces || myWorkspaces.length === previousLength) {
              myWorkspacesHasMore = false
              break
            }
          }
        }
        resolve(response)
      } catch(e) {
        reject(e)
      }
    })
    try {
      await getMyWorkspacesPromise
    } finally {
      getMyWorkspacesPromise = null
    }
  }

  async function loadMoreMyWorkspaces() {
    if(!myWorkspaces || !myWorkspacesHasMore || isLoadingMyWorkspaces) {
      return
    }
    await fetchMyWorkspacesPage(true)
  }

  function resetMyWorkspaces() {
    leaveAllWorkspaces()
    myWorkspaces = null
    myWorkspacesHasMore = false
    myWorkspacesSkip = 0
    hasJoinedAllWorkspaces = false
    getMyWorkspacesPromise = null
  }

  const shouldShowLoadMoreMyWorkspaces = $computed(() => {
    if(!myWorkspaces) {
      return false
    }
    return myWorkspacesHasMore
  })

  const resourcePathFilter = {
    resourceType: () => ResourceType.Workspace,
    resourceId: () => currentWorkspace?.id,
  }

  const {start, stop} = useActivityItemSocketListener(ActivityItemType.WorkspaceDeleted, ({targetResource}) => {
    if(!route.value.meta.requireWorkspace) {
      return
    }
    messages.info(i18n.global.t('workspaces.workspaceHasBeenDeleted'))
    router.push({
      name: RouteName.ChooseWorkspace,
    })
    leaveWorkspaceRoom(targetResource.resourceId)
  }, resourcePathFilter, {
    useLifecycleHooks: false,
  })

  function setCurrentWorkspacePermissionMap(permissions: Pick<IPermissionResponseInstance, 'type'|'value'>[]) {
    const newPermissionMap = new Map<PermissionType, unknown>()
    for(const permission of permissions) {
      newPermissionMap.set(permission.type, permission.value)
    }
    workspacePermissionMap = newPermissionMap
  }

  function hasPermission(permissionType: PermissionType): boolean {
    const permissionMap = workspacePermissionMap
    if(!permissionMap?.has(permissionType)) {
      return false
    }
    const value = permissionMap.get(permissionType)
    return !!value
  }

  function joinAllWorkspaces() {
    if(!myWorkspaces || hasJoinedAllWorkspaces) {
      return
    }
    if(isGodModeEnabled()) {
      if(currentWorkspace?.id) {
        joinWorkspaceRoom(currentWorkspace.id)
      }
      hasJoinedAllWorkspaces = true
      return
    }
    for(const workspace of myWorkspaces) {
      joinWorkspaceRoom(workspace.id)
    }
  }

  function leaveAllWorkspaces() {
    for(const workspaceId of [...joinedWorkspaceRoomIds]) {
      leaveWorkspaceRoom(workspaceId)
    }
  }

  function joinWorkspaceRoom(workspaceId: string) {
    if(joinedWorkspaceRoomIds.has(workspaceId)) {
      return
    }
    joinedWorkspaceRoomIds.add(workspaceId)
    joinResourceGlobally(workspaceRoomHolderId, ResourceType.Workspace, workspaceId)
  }

  function leaveWorkspaceRoom(workspaceId: string) {
    joinedWorkspaceRoomIds.delete(workspaceId)
    leaveResourceGlobally(workspaceRoomHolderId, ResourceType.Workspace, workspaceId)
  }

  watch(() => {
    return authStore.currentEntity ? `${authStore.currentEntity.entityType}:${authStore.currentEntity.entityId}` : null
  }, async (value, oldValue) => {
    if(value === oldValue) {
      return
    }
    if(oldValue) {
      resetMyWorkspaces()
    }
    if(!value) {
      resetMyWorkspaces()
      return
    }

    if(authStore.currentEntity?.entityType !== EntityType.User) {
      myWorkspaces = []
      return
    }

    await ensureMyWorkspaces()
    joinAllWorkspaces()
  })

  watch(() => {
    return socketStore.isConnected
  }, value => {
    if(value) {
      joinAllWorkspaces()
    } else {
      hasJoinedAllWorkspaces = false
    }
  })

  const myWorkspaceIds = $computed(() => {
    if(!myWorkspaces) {
      return []
    }
    return myWorkspaces.map(workspace => workspace.id)
  })

  function onUserJoinedResource(payload: IEntityJoinedResourceEmitPayload) {
    if(!myWorkspaceIds.includes(payload.resourceId) || payload.resourceType !== ResourceType.Workspace) {
      return
    }
    if(!payload.entity) {
      return
    }
    addEntityToEntitiesInResource(payload.entity, payload.resourceId)
  }

  function handleWorkspaceAccessLost(workspaceId: string, message: string) {
    revokedAtByWorkspaceId.set(workspaceId, Date.now())
    leaveWorkspaceRoom(workspaceId)
    const removedWorkspace = myWorkspaces?.find(workspace => workspace.id === workspaceId)
      ?? (currentWorkspace?.id === workspaceId ? currentWorkspace : null)
    if(removedWorkspace) {
      cacheStore.setCacheItem(`workspace-by-name-${removedWorkspace.name}`, null)
    }
    myWorkspaces = myWorkspaces?.filter(workspace => workspace.id !== workspaceId) ?? myWorkspaces
    const newEntitiesInResourceMap = new Map(entitiesInResourceMap)
    newEntitiesInResourceMap.delete(workspaceId)
    entitiesInResourceMap = newEntitiesInResourceMap
    if(currentWorkspace?.id !== workspaceId) {
      return
    }
    currentWorkspace = null
    workspacePermissionMap = null
    if(!route.value.meta.requireWorkspace) {
      return
    }
    messages.info(message)
    router.push({
      name: RouteName.ChooseWorkspace,
    })
  }

  function onWorkspaceMemberDeleted(payload: IWorkspaceMemberDeletedEmitPayload) {
    handleWorkspaceAccessLost(payload.workspaceId, i18n.global.t('workspaces.youHaveBeenRemoved'))
  }

  function onResourceAccessRevoked(payload: IResourceAccessRevokedEmitPayload) {
    if(payload.resourceType !== ResourceType.Workspace) {
      return
    }
    handleWorkspaceAccessLost(payload.resourceId, i18n.global.t('workspaces.noAccessToWorkspace'))
  }

  function onUserLeftResource(payload: IEntityLeftResourceEmitPayload) {
    if(!myWorkspaceIds.includes(payload.resourceId) || payload.resourceType !== ResourceType.Workspace) {
      return
    }
    if(!payload.entity) {
      return
    }
    removeEntityFromEntitiesInResource(payload.entity, payload.resourceId)
  }

  function onUsersInResource(payload: IEntitiesInResourceEmitPayload) {
    if(!myWorkspaceIds.includes(payload.resourceId)) {
      return
    }
    setEntitiesInResource(payload.resourceId, payload.entities)
  }

  function onUserUpdated(payload: IToJson<IUserUpdatedEmitPayload>) {
    const mappedUser = mapUserResponseInstance(payload.user)
    for(const workspaceId of myWorkspaceIds) {
      const newMap = new Map(entitiesInResourceMap)
      if(!newMap.has(workspaceId)) {
        continue
      }
      const entities = newMap.get(workspaceId)
      const userKey = getEntityKey(EntityType.User, payload.userId)
      const entityIndex = entities.findIndex(entity => getEntityKeyByEntity(entity) === userKey)
      if(entityIndex === -1) {
        continue
      }
      entities[entityIndex] = {
        entityType: EntityType.User,
        entityId: payload.userId,
        payload: mappedUser,
      }
      newMap.set(workspaceId, entities)
      entitiesInResourceMap = newMap
    }
  }

  function addEntityToEntitiesInResource(entity: IResponseEntity, workspaceId: string) {
    const newMap = new Map(entitiesInResourceMap)
    if(!newMap.has(workspaceId)) {
      newMap.set(workspaceId, [])
    }
    const entities = newMap.get(workspaceId)
    const entityKey = getEntityKeyByEntity(entity)
    const isAlreadyIncluded = entities.some(existingEntity => getEntityKeyByEntity(existingEntity) === entityKey)
    if(!isAlreadyIncluded) {
      entities.push(entity)
    }
    entitiesInResourceMap = newMap
  }

  function removeEntityFromEntitiesInResource(entity: IResponseEntity, workspaceId: string) {
    const newMap = new Map(entitiesInResourceMap)
    if(!newMap.has(workspaceId)) {
      return
    }
    const leavingKey = getEntityKeyByEntity(entity)
    let entities = newMap.get(workspaceId)
    entities = entities.filter(existingEntity => {
      return getEntityKeyByEntity(existingEntity) !== leavingKey
    })
    newMap.set(workspaceId, entities)
    entitiesInResourceMap = newMap
  }

  function setEntitiesInResource(workspaceId: string, entities: IResponseEntity[]) {
    const newMap = new Map(entitiesInResourceMap)
    newMap.set(workspaceId, Array.isArray(entities) ? entities.filter(Boolean) : [])
    entitiesInResourceMap = newMap
  }

  const entitiesInCurrentWorkspace = $computed(() => {
    return entitiesInResourceMap.get(currentWorkspace?.id)
  })

  return $$({
    currentWorkspace,
    myWorkspaces,
    myWorkspacesHasMore,
    isLoadingMyWorkspaces,
    shouldShowLoadMoreMyWorkspaces,
    workspacePermissionMap,
    setCurrentWorkspacePermissionMap,
    hasPermission,
    entitiesInCurrentWorkspace,
    initialize,
    dispose,
    entitiesInResourceMap,
    leaveWorkspaceRoom,
    ensureMyWorkspaces,
    loadMoreMyWorkspaces,
    resetMyWorkspaces,
    fetchMyWorkspacesPage,
  })
})
